import { useEffect, useRef } from 'react';

import ConversationSummaryHelper from 'app/utils/conversationSummaryHelper';
import { ConversationPreview } from 'components/conversation/conversationPreview';
import { pathWithBasename } from 'utils/pathWithBasename';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { createAppUrl } from 'utils/url/createAppUrl';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import styles from './conversationPrint.view.module.scss';

/**
 * A chromeless view that renders a single conversation for printing. Opened in
 * its own window by the conversation "Print transcript" actions. The behaviour
 * is driven by search params: `print=true` auto-opens the print dialog once the
 * conversation has loaded, and `showInternalDetails=true` includes internal
 * notes, tags and custom attributes.
 */
export const ConversationPrintView = (): JSX.Element => {
  const { csid } = useAppPathParams('/conversation/:csid/print');
  const { params } = useAppSearchParams('/conversation/:csid/print');

  const shouldPrint = params.print === 'true';
  const showInternalDetails = params.showInternalDetails === 'true';
  const showCustomer = params.showCustomer === 'true';

  const asyncStatus = useAppSelector(
    (state) => state.conversation.asyncStatus.getConversation,
  );
  const metaConversation = useAppSelector(
    (state) => state.conversation.metaConversation,
  );

  // `asyncStatus` starts as 'succeeded' on a fresh store, so it can't be trusted
  // on its own, the requested conversation isn't actually loaded until its id is
  // in the store.
  const isLoaded =
    asyncStatus === 'succeeded' &&
    String(metaConversation.conversation.id) === csid;
  const hasPrinted = useRef(false);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    const subject = new ConversationSummaryHelper(metaConversation)
      .getSubject()
      ?.trim();

    // Give the print window a meaningful default PDF filename.
    document.title = subject
      ? `Conversation ${csid} - ${subject}`
      : `Conversation ${csid}`;
  }, [isLoaded, metaConversation, csid]);

  // Auto-open the print dialog once the conversation has fully rendered.
  useEffect(() => {
    if (!shouldPrint || !isLoaded || hasPrinted.current) {
      return;
    }

    hasPrinted.current = true;
    let cancelled = false;

    const fontsReady = document.fonts?.ready ?? Promise.resolve();

    const imagesReady = Promise.all(
      Array.from(document.images)
        .filter((image) => !image.complete)
        .map(
          (image) =>
            new Promise<void>((resolve) => {
              image.addEventListener('load', () => resolve(), { once: true });
              image.addEventListener('error', () => resolve(), { once: true });
            }),
        ),
    );

    Promise.all([fontsReady, imagesReady]).then(() => {
      if (cancelled) {
        return;
      }

      requestAnimationFrame(() => {
        if (!cancelled) {
          window.print();
        }
      });
    });

    return () => {
      cancelled = true;
    };
  }, [shouldPrint, isLoaded]);

  return (
    <div className={styles.root}>
      <ConversationPreview
        csid={Number(csid)}
        highlights={{}}
        showInternalDetails={showInternalDetails}
        showActions={false}
        showCustomer={showCustomer}
        showRecipients={shouldPrint}
        onPreviewClose={() => window.close()}
        onGoToConversation={() => {
          window.location.href = pathWithBasename(
            createAppUrl({
              path: '/conversation/:csid',
              params: { path: { csid } },
            }),
          );
        }}
      />
    </div>
  );
};
